import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { User, Calendar, Brain, FileText, Sparkles, ArrowRight, CheckCircle, Target } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserCoach, checkCoachAIAccess } from '../../lib/auth';
import { ResultsGraph } from '../../components/ResultsGraph';
import { HarmonicGraph } from '../../components/HarmonicGraph';
import { AIAnalysisPanel } from '../../components/ai/AIAnalysisPanel';
import { CoachProgressView } from '../../components/coach/CoachProgressView';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import type { AssessmentResult } from '../../types/results';

export function SharedResultDetail() {
  const navigate = useNavigate();
  const { shareId } = useParams<{ shareId: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [share, setShare] = useState<{
    share_id: string;
    result_id: string;
    shared_by_name: string;
    shared_by_email: string;
    message: string | null;
    status: string;
    created_at: string;
    result: {
      id: string;
      user_id: string;
      scores: any;
      harmonic_scores: any;
      completed_at: string;
      coach_ai_analysis: string | null;
    };
  } | null>(null);
  const [analysis, setAnalysis] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'profile' | 'progress'>('profile');

  useEffect(() => {
    if (authLoading) return;
    if (!user || !shareId) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) {
          navigate('/login');
          return;
        }

        const { data: coach } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) {
          navigate('/login');
          return;
        }

        const aiAccess = await checkCoachAIAccess(user.id);
        setHasAIAccess(aiAccess);

        const { data, error } = await supabase
          .rpc('fetch_received_shares', {
            p_recipient_type: 'coach',
            p_recipient_id: coach.id,
          });

        if (error) throw error;

        const found = (data || []).find((row: any) => row.share_id === shareId);

        if (!found) {
          navigate('/coach/respondents');
          return;
        }

        const mapped = {
          share_id: found.share_id,
          result_id: found.result_id,
          shared_by_name: found.sharer_name || 'Respondent',
          shared_by_email: found.sharer_email || '',
          message: found.message,
          status: found.status,
          created_at: found.created_at,
          result: {
            id: found.result_id,
            user_id: found.result_user_id,
            scores: found.result_scores,
            harmonic_scores: found.result_harmonic_scores,
            completed_at: found.result_completed_at,
            coach_ai_analysis: found.result_coach_ai_analysis || null,
          },
        };

        setShare(mapped);
        setAnalysis(mapped.result.coach_ai_analysis || undefined);
      } catch (err) {
        console.error('Error loading shared result:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate, shareId]);

  const handleAnalysisUpdate = (newAnalysis: string) => {
    setAnalysis(newAnalysis);
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading shared result..." />;
  }

  if (!share) {
    return (
      <div className="min-h-screen freud-bg py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="freud-card-elevated p-8">
            <FileText className="w-16 h-16 text-cream-400 mx-auto mb-4" />
            <h2 className="freud-heading-xl text-chocolate-900 mb-2">Shared Result Not Found</h2>
            <p className="text-chocolate-500 mb-6">
              This shared result may have been revoked or is no longer available.
            </p>
            <Link to="/coach/respondents" className="freud-btn-primary inline-flex items-center gap-2">
              Back to Respondents
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const mapped: AssessmentResult = {
    id: share.result.id,
    userId: share.result.user_id,
    scores: share.result.scores || [],
    harmonicScores: share.result.harmonic_scores || [],
    completedAt: new Date(share.result.completed_at || Date.now()),
    version: 1,
  };

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-sage-100 rounded-xl flex items-center justify-center">
                <User className="w-6 h-6 text-sage-600" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">{share.shared_by_name}</h1>
                <div className="flex items-center gap-2 text-sm text-chocolate-500">
                  <Calendar className="w-4 h-4" />
                  Completed: {mapped.completedAt.toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Tab switcher */}
              <div className="flex bg-cream-100 rounded-xl p-1 gap-1">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'profile' ? 'bg-white text-chocolate-900 shadow-sm' : 'text-chocolate-500 hover:text-chocolate-700'}`}
                >
                  <Brain className="w-4 h-4" />
                  Profile
                </button>
                <button
                  onClick={() => setActiveTab('progress')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'progress' ? 'bg-white text-chocolate-900 shadow-sm' : 'text-chocolate-500 hover:text-chocolate-700'}`}
                >
                  <Target className="w-4 h-4" />
                  Progress
                </button>
              </div>

              <Link
                to="/coach/respondents"
                className="flex items-center gap-2 text-chocolate-500 hover:text-sage-600 transition-colors text-sm font-medium"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back to Respondents
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {activeTab === 'profile' ? (
          <div className="space-y-8">
            <div className="freud-card-elevated p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                  <span className="text-sm font-bold text-sage-600">
                    {share.shared_by_name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-chocolate-900">{share.shared_by_name}</p>
                  <p className="text-xs text-chocolate-400">{share.shared_by_email}</p>
                </div>
                {share.status === 'accepted' && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sage-100 text-sage-700 text-sm font-medium ml-auto">
                    <CheckCircle className="w-4 h-4" />
                    Accepted
                  </span>
                )}
              </div>
              {share.message && (
                <div className="mt-3 p-3 rounded-xl bg-cream-50 text-chocolate-600 text-sm italic">
                  "{share.message}"
                </div>
              )}
              <p className="text-xs text-chocolate-400 mt-2">
                Shared on {new Date(share.created_at).toLocaleDateString()}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div className="freud-card-elevated p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                    <Brain className="w-5 h-5 text-sage-600" />
                  </div>
                  <h2 className="freud-heading-xl text-chocolate-900">Characteristic Profile</h2>
                </div>
                <ResultsGraph scores={mapped.scores} />
              </div>

              <div className="freud-card-elevated p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-coral-100 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-coral-600" />
                  </div>
                  <h2 className="freud-heading-xl text-chocolate-900">Harmonic Scale Profile</h2>
                </div>
                <HarmonicGraph scores={mapped.harmonicScores} />
              </div>
            </div>

            {hasAIAccess && (
              <AIAnalysisPanel
                analysis={analysis}
                scores={mapped.scores}
                harmonicScores={mapped.harmonicScores}
                resultId={share.result_id}
                showRegenerate
                respondentName={share.shared_by_name}
                storageField="coach_ai_analysis"
                onAnalysisUpdate={handleAnalysisUpdate}
              />
            )}
          </div>
        ) : (
          share.result.user_id && <CoachProgressView userId={share.result.user_id} />
        )}
      </main>
    </div>
  );
}
